import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import { TextField, Toggle, Section } from '@/admin/AdminUI';
import ImageUploader from '@/admin/ImageUploader';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

// News widget admin — same CRUD shape as FAQTab. Placement on the public
// section is derived from list order: item 1 renders as the large featured
// story, items 2-3 as the secondary cards, and everything after as the
// title-only TOP HEADLINES list.
// The public section shows 1 featured + 2 cards + 8 headlines.
const PUBLIC_DISPLAY_LIMIT = 11;

// Same rule as the backend validator: really parse it, and require an
// http(s) scheme AND a nonempty hostname — "https://?" is not a link.
const isValidArticleUrl = (u) => {
  try {
    const p = new URL(u);
    return (p.protocol === 'http:' || p.protocol === 'https:') && p.hostname.length > 0;
  } catch (e) {
    return false;
  }
};

export default function NewsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // One visible error line for every request path. A failed load must
  // never masquerade as "No news articles yet".
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  // ---- Video shelf beneath the news grid (up to 3 YouTube videos). ----
  // vids: undefined = UNRESOLVED (controls locked, fail-closed),
  // {rev, items} = resolved. Every mutation is a compare-and-set
  // against rev — a stale save gets 409 and can never overwrite a
  // newer change. Video errors are fully independent from article
  // errors.
  const [vids, setVids] = useState(undefined);
  const [vidsFailed, setVidsFailed] = useState(false);
  const [vidsBusy, setVidsBusy] = useState(false);
  const [vidsError, setVidsError] = useState('');
  const [videoUrl, setVideoUrl] = useState('');        // Add input buffer
  const [replacingId, setReplacingId] = useState(null); // row in inline-replace mode
  const [replaceUrl, setReplaceUrl] = useState('');

  // load() never clears `error` itself: a mutation that failed sets the
  // message, and a subsequent successful refresh must not wipe it before
  // the admin has read it. Each mutation's SUCCESS path clears the error.
  const load = async () => {
    try {
      const r = await api.get('/admin/news');
      setItems(r.data || []);
      setLoaded(true);
    } catch (e) {
      setError('Could not load the news list. Refresh the page or try again.');
    }
  };
  // Request-generation guard for the videos GET. StrictMode's duplicate
  // mount, rapid Retry clicks, and slow responses can overlap; only the
  // NEWEST generation may write state, and every successful mutation
  // bumps the generation first so a stale GET landing afterwards can
  // never overwrite mutation truth. loadingRef makes Retry single-flight.
  const videoReqGenRef = useRef(0);
  const vidsLoadingRef = useRef(false);
  // Synchronous mutation OWNERSHIP token: acquired before any PUT is
  // issued, so two rapid callbacks produce exactly one request even
  // before React re-renders the busy state. Only the owning request
  // may release it.
  const vidsMutationRef = useRef(null);
  // Focus management for the inline Replace workflow.
  const replaceInputRef = useRef(null);
  const replaceBtnRefs = useRef({});
  const replaceReturnIdRef = useRef(null);
  // Synchronous mirror of replacingId (async handlers read it after
  // awaits, when the state value they closed over may be stale), plus
  // the stable-target fallback used when the row being replaced was
  // deleted by another admin.
  const replacingIdRef = useRef(null);
  const stableFocusPendingRef = useRef(false);
  const addInputRef = useRef(null);
  const vidsCountRef = useRef(null);

  // After EVERY accepted server list: if the row open in Replace mode
  // no longer exists (another admin deleted it), exit Replace mode,
  // clear its input, and queue a focus move to a stable visible target
  // — never let the unmounting input drop focus on the page body.
  const reconcileReplaceMode = (items) => {
    const rid = replacingIdRef.current;
    if (!rid) return;
    if (items.some((it) => it && it.item_id === rid)) return;
    replaceReturnIdRef.current = null; // its Replace button is gone too
    stableFocusPendingRef.current = true;
    replacingIdRef.current = null;
    setReplacingId(null);
    setReplaceUrl('');
  };

  const isValidYtId = (v) => typeof v === 'string' && /^[A-Za-z0-9_-]{11}$/.test(v);

  // Mirror of the backend's URL parser and format classifier, so the
  // admin can verify that every returned source URL is a real YouTube
  // link whose parsed id and classification match what the item claims.
  const parseYtIdFromUrl = (url) => {
    try {
      const u = new URL((url || '').trim());
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      const host = (u.hostname || '').toLowerCase();
      const segs = u.pathname.split('/').filter(Boolean);
      let cand = null;
      if (host === 'youtu.be') cand = segs[0] || null;
      else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(host)) {
        if (u.pathname === '/watch') cand = u.searchParams.get('v');
        else if (segs.length === 2 && ['shorts', 'embed', 'live'].includes(segs[0])) cand = segs[1];
      }
      return isValidYtId(cand) ? cand : null;
    } catch (e) { return null; }
  };
  const ytFormatFromUrl = (url) => {
    try {
      const u = new URL((url || '').trim());
      const host = (u.hostname || '').toLowerCase();
      const segs = u.pathname.split('/').filter(Boolean);
      return ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(host) && segs.length === 2 && segs[0] === 'shorts' ? 'short' : 'standard';
    } catch (e) { return 'standard'; }
  };

  // DEEP response validation — every rule the server enforces is
  // re-checked before the UI unlocks. A response like
  // {"rev":1,"items":[null]} must lock the controls, never render.
  const isValidVideosShape = (d) => {
    if (!d || typeof d !== 'object' || Array.isArray(d)) return false;
    if (typeof d.rev !== 'number' || !Number.isInteger(d.rev) || d.rev < 0) return false;
    if (!Array.isArray(d.items) || d.items.length > 3) return false;
    const ids = new Set();
    const vids2 = new Set();
    for (const it of d.items) {
      if (!it || typeof it !== 'object' || Array.isArray(it)) return false;
      if (typeof it.item_id !== 'string' || it.item_id === '' || ids.has(it.item_id)) return false;
      ids.add(it.item_id);
      if (!isValidYtId(it.video_id) || vids2.has(it.video_id)) return false;
      vids2.add(it.video_id);
      if (typeof it.source_url !== 'string') return false;
      // The source must be a real YouTube link whose PARSED id equals
      // the claimed video_id, and whose real classification equals the
      // claimed display_format — a malformed URL, an id mismatch, or a
      // format mismatch all fail closed.
      const parsedId = parseYtIdFromUrl(it.source_url);
      if (parsedId === null || parsedId !== it.video_id) return false;
      if (it.display_format !== 'short' && it.display_format !== 'standard') return false;
      if (it.display_format !== ytFormatFromUrl(it.source_url)) return false;
    }
    if (d.items.length >= 2 && d.items.some((it) => it.display_format !== 'short')) return false;
    return true;
  };

  // Fail-closed: unresolved on every load start; a failed GET (or a
  // malformed 200 — never treated as an empty list) locks the controls
  // with a dedicated error + Retry.
  // Returns a DEFINITIVE boolean: true ONLY after a valid server list
  // has been accepted, stored, and reconciled; false for a network
  // failure, a malformed 200, a stale generation, or the single-flight
  // early return — callers use it to decide whether the on-screen list
  // may honestly be called "the current saved version".
  const loadVideos = async () => {
    if (vidsLoadingRef.current) return false; // single-flight
    vidsLoadingRef.current = true;
    const gen = videoReqGenRef.current + 1;
    videoReqGenRef.current = gen;
    setVids(undefined);
    setVidsFailed(false);
    try {
      const r = await api.get('/admin/news-videos');
      if (gen !== videoReqGenRef.current) return false; // superseded — discard
      if (!isValidVideosShape(r.data)) { setVidsFailed(true); return false; }
      setVids({ rev: r.data.rev, items: r.data.items });
      // A successful load shows a healthy list — any earlier lock/action
      // warning is now stale and would be a lie next to it. (The 409
      // conflict flow sets its message AFTER this reload, so that one
      // survives.)
      setVidsError('');
      reconcileReplaceMode(r.data.items);
      return true;
    } catch (e) {
      if (gen !== videoReqGenRef.current) return false; // superseded — discard
      // A failed Retry keeps the failure banner (and any existing
      // message) honest and visible.
      setVidsFailed(true);
      return false;
    } finally {
      vidsLoadingRef.current = false;
    }
  };
  useEffect(() => { load(); loadVideos(); }, []);

  const vidsReady = vids !== undefined && !vidsFailed;

  // One mutation path for add / replace / remove / reorder: sends the
  // rev we loaded; the server applies the whole change atomically or
  // answers 409 when someone else's change won.
  const videoOp = async (op) => {
    // Synchronous exclusion FIRST (the ref flips before any await), so
    // two rapid callbacks issue exactly one PUT; the React busy state
    // is display-only, never the lock.
    if (vidsMutationRef.current || !vidsReady) return false;
    const token = {};
    vidsMutationRef.current = token;
    setVidsBusy(true);
    try {
      const r = await api.put('/admin/news-videos', { rev: vids.rev, ...op });
      videoReqGenRef.current += 1; // stale GETs can no longer write
      if (!isValidVideosShape(r.data)) {
        // Malformed mutation response: LOCK the controls — never
        // pretend the reply was a valid or empty list.
        setVids(undefined);
        setVidsFailed(true);
        setVidsError('The server reply was unreadable — the controls are locked. Use Retry to reload the saved list.');
        return false;
      }
      setVids({ rev: r.data.rev, items: r.data.items });
      setVidsError('');
      reconcileReplaceMode(r.data.items);
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        // Someone else's change was saved first: reload the truth
        // FIRST, then set the conflict message — the reload's own
        // success-cleanup would otherwise erase it before Sol reads it.
        // The message only claims the list is current when the reload
        // DEFINITIVELY verified it; an unverified reload leaves the
        // controls locked and says so plainly.
        const verified = await loadVideos();
        setVidsError(verified
          ? 'Another change was saved before yours, so yours was NOT applied. The list below is the current saved version — check it and try again.'
          : 'Another change was saved before yours, so yours was NOT applied. The saved list could not be verified. Use Retry before making another change.');
      } else {
        setVidsError(e?.response?.data?.detail || 'Could not save the video change — try again.');
      }
      return false;
    } finally {
      // Only the OWNING request releases the lock.
      if (vidsMutationRef.current === token) {
        vidsMutationRef.current = null;
        setVidsBusy(false);
      }
    }
  };

  const addVideo = async () => {
    const url = (videoUrl || '').trim();
    if (!url) { setVidsError('Paste a YouTube link first.'); return; }
    if (await videoOp({ op: 'add', url })) setVideoUrl('');
  };

  const startReplace = (it) => {
    replaceReturnIdRef.current = it.item_id;
    replacingIdRef.current = it.item_id;
    setReplacingId(it.item_id);
    setReplaceUrl('');
  };
  const cancelReplace = () => {
    replacingIdRef.current = null;
    setReplacingId(null);
    setReplaceUrl('');
  };
  const saveReplace = async () => {
    const url = (replaceUrl || '').trim();
    if (!url) { setVidsError('Paste the replacement YouTube link first.'); return; }
    if (await videoOp({ op: 'replace', item_id: replacingId, url })) cancelReplace();
  };

  // Replace-mode focus: entering moves focus into the replacement
  // input; leaving (Cancel or successful save) returns it to that
  // row's Replace button — never dropped on the document body. The
  // row's item_id survives a replace (the server keeps it stable), so
  // the return target still exists.
  // Replace focus authority — a LAYOUT effect keyed on BOTH the
  // replace state and the accepted list. A reload unmounts the Replace
  // controls (vids -> undefined) and drops focus on the body; when the
  // accepted refreshed list still contains the row, replacingId never
  // changes, so a replacingId-only effect would never rerun — keying
  // on vids makes the remount refocus its input. Focus is only taken
  // when the document actually LOST it (activeElement missing, body,
  // or documentElement) — never stolen from a control the admin
  // deliberately moved to. Entry into Replace mode (replacingId
  // changed) always focuses the input, as before.
  const prevReplacingIdRef = useRef(null);
  useLayoutEffect(() => {
    const prev = prevReplacingIdRef.current;
    prevReplacingIdRef.current = replacingId;
    if (replacingId) {
      const entering = prev !== replacingId;
      const ae = document.activeElement;
      const focusLost = !ae || ae === document.body || ae === document.documentElement;
      if (replaceInputRef.current && (entering || focusLost)) {
        replaceInputRef.current.focus();
      }
      return;
    }
    if (replaceReturnIdRef.current) {
      // Normal Cancel / successful Replace: back to that row's button.
      const btn = replaceBtnRefs.current[replaceReturnIdRef.current];
      replaceReturnIdRef.current = null;
      if (btn && typeof btn.focus === 'function') { btn.focus(); return; }
    }
    if (stableFocusPendingRef.current) {
      // The row (and its Replace button) no longer exist: land on a
      // stable visible target in the Videos section instead of the
      // page body — the Add input, else the first remaining Replace
      // button, else the focusable count/status line.
      stableFocusPendingRef.current = false;
      const firstBtn = Object.values(replaceBtnRefs.current).find((el) => el && el.isConnected);
      const target = (addInputRef.current && addInputRef.current.isConnected && addInputRef.current)
        || firstBtn
        || vidsCountRef.current;
      if (target && typeof target.focus === 'function') target.focus();
    }
  }, [replacingId, vids]);

  const removeVideoItem = async (it) => {
    if (!window.confirm('Remove this video from the news section?')) return;
    await videoOp({ op: 'remove', item_id: it.item_id });
  };

  const moveVideo = async (it, dir) => {
    const ids = vids.items.map((v) => v.item_id);
    const idx = ids.indexOf(it.item_id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    await videoOp({ op: 'reorder', item_ids: ids });
  };

  const addNew = () => setEditing({ title: '', summary: '', tag: '', image: '', url: '', visible: true });
  const startEdit = (it) => setEditing({ ...it });

  // Mutations reload ONLY after success, so a failure message stays on
  // screen instead of being wiped by the refresh that follows it.
  const remove = async (id) => {
    if (!window.confirm('Delete this news article?')) return;
    try {
      await api.delete(`/admin/news/${id}`);
      setError('');
      load();
    } catch (e) {
      setError('Delete failed. The article is unchanged — try again.');
    }
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const prev = items;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await api.put('/admin/news/reorder', { items: next.map((it, i) => ({ id: it.id, order: i + 1 })) });
      setError('');
      load();
    } catch (e) {
      // The backend writes reorder changes one row at a time, so a failed
      // request may have PARTIALLY saved before dying — neither `next` nor
      // `prev` is trustworthy. Re-read the server: its response IS the
      // saved order. Set the error after the reload so it stays visible.
      try {
        const r = await api.get('/admin/news');
        setItems(r.data || []);
        setError('Reorder failed. The list has been refreshed to the saved order — try again.');
      } catch (e2) {
        // Could not verify what the server holds. Show the pre-move copy
        // but say plainly that it is unverified.
        setItems(prev);
        setError('Reorder failed and the saved order could not be verified. Refresh the page before making more changes.');
      }
    }
  };

  // Order is deliberately absent: the server assigns it on create and
  // ignores it on ordinary edits. Only the arrow buttons change order.
  const payloadFrom = (src, overrides = {}) => ({
    title: (src.title || '').trim(),
    summary: src.summary || '',
    tag: src.tag || '',
    image: src.image || '',
    url: (src.url || '').trim(),
    visible: src.visible !== false,
    ...overrides,
  });

  const save = async () => {
    const title = (editing.title || '').trim();
    const url = (editing.url || '').trim();
    if (!title) { setError('A title is required.'); return; }
    if (!isValidArticleUrl(url)) {
      setError('The article link must be a complete address starting with http:// or https://');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFrom(editing);
      if (editing.id) {
        await api.put(`/admin/news/${editing.id}`, payload);
      } else {
        await api.post('/admin/news', payload);
      }
      setEditing(null);
      setError('');
      load();
    } catch (e) {
      setError(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed. Nothing was changed — try again.');
    } finally { setSaving(false); }
  };

  const toggleVisible = async (it) => {
    try {
      await api.put(`/admin/news/${it.id}`, payloadFrom(it, { visible: !(it.visible !== false) }));
      setError('');
      load();
    } catch (e) {
      setError('Could not change visibility. The article is unchanged — try again.');
    }
  };

  // Which public slot each row occupies. Hidden rows hold no slot, and
  // ranks are counted among VISIBLE rows only, so hiding an article
  // promotes the ones after it exactly as the live section will.
  const visibleRankById = {};
  let _rank = 0;
  for (const it of items) {
    if (it.visible !== false) { _rank += 1; visibleRankById[it.id] = _rank; }
  }
  const slotLabel = (it) => {
    if (it.visible === false) return 'Hidden';
    const rank = visibleRankById[it.id];
    if (rank === 1) return 'Featured';
    if (rank <= 3) return 'Card';
    if (rank <= PUBLIC_DISPLAY_LIMIT) return 'Headline';
    return 'Not shown';
  };

  return (
    <div>
      <Section
        title="News Articles"
        action={<button onClick={addNew} className="btn-primary !py-2 !px-4 text-[12px]" data-testid="news-add"><Plus size={14} className="mr-1" /> Add Article</button>}
      >
        <p className="font-body text-[12px] text-ink-muted -mt-2">
          Shown on the homepage above the FAQ. The first visible article is the large featured story, the next two are the smaller cards, and the following eight appear in the Top Headlines list. Use the arrows to change order. Clicking any of them sends visitors straight to the article link.
        </p>
        {error && (
          <p className="font-body text-[12px] font-semibold text-brand-red border border-brand-red/40 bg-brand-red/5 rounded-sm px-3 py-2" data-testid="news-admin-error">
            {error}
          </p>
        )}
        <div className="space-y-2.5">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white"
              data-testid="news-row-admin"
            >
              <span className="font-display font-bold text-[12px] text-ink-soft w-6 text-center">{it.order}</span>
              <div className="w-16 h-11 border border-border-line rounded-sm bg-bg-canvas overflow-hidden shrink-0 flex items-center justify-center">
                {it.image ? (
                  <img src={fileUrl(it.image) || it.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-body text-[9px] uppercase text-ink-soft">No image</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold uppercase text-ink-primary text-[13px] tracking-tightish truncate">{it.title}</div>
                <div className="font-body text-[11px] text-ink-muted line-clamp-1">{it.tag ? `${it.tag} — ` : ''}{it.url}</div>
              </div>
              <span className="font-body text-[10px] uppercase tracking-wider2 text-ink-soft w-16 text-center">{slotLabel(it)}</span>
              <span className="font-body text-[11px] uppercase tracking-wider2 text-ink-soft">
                {it.visible !== false ? 'Visible' : 'Hidden'}
              </span>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => toggleVisible(it)} className="btn-secondary !py-2 !px-3 text-[11px]" data-testid="news-toggle-visible">
                {it.visible !== false ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => startEdit(it)} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
              <button onClick={() => remove(it.id)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
          {loaded && !error && items.length === 0 && (
            <p className="font-body text-[12px] text-ink-muted py-4">No news articles yet. The homepage section stays hidden until the first visible article is added.</p>
          )}
        </div>
      </Section>

      <Section title="Videos">
        <p className="font-body text-[12px] text-ink-muted -mt-2">
          Up to three YouTube videos shown directly beneath the news grid — no title, and completely hidden when none are set. Shorts links show as tall phone-shaped players side by side; a regular (widescreen) video is allowed only by itself. Use the arrows to set the order.
        </p>
        {vidsFailed && (
          <p role="alert" className="font-body text-[12px] font-semibold text-brand-red border border-brand-red/40 bg-brand-red/5 rounded-sm px-3 py-2" data-testid="news-videos-load-error">
            Could not load the current videos — the controls are locked so the real state is never misrepresented.
            <button onClick={loadVideos} className="btn-secondary !py-1 !px-3 text-[11px] ml-3" data-testid="news-videos-retry">Retry</button>
          </p>
        )}
        {vids === undefined && !vidsFailed && (
          <p className="font-body text-[12px] text-ink-muted py-2">Loading videos…</p>
        )}
        {/* Action/conflict/lock errors render OUTSIDE the ready block,
            so the detailed message stays visible while the controls
            are locked — not only after they come back. */}
        {vidsError && (
          <p role="alert" className="font-body text-[12px] font-semibold text-brand-red" data-testid="news-videos-action-error">{vidsError}</p>
        )}
        {vidsReady && (
          <>
            <p ref={vidsCountRef} tabIndex={-1} className="font-body text-[11px] font-semibold uppercase tracking-wider2 text-ink-soft focus:outline-none" data-testid="news-videos-count">
              {vids.items.length} of 3
            </p>
            <div className="space-y-2.5">
              {vids.items.map((it, idx) => (
                <div key={it.item_id} className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white" data-testid="news-video-row">
                  <span className="font-display font-bold text-[12px] text-ink-soft w-5 text-center">{idx + 1}</span>
                  <span className={`shrink-0 font-body font-bold uppercase text-[10px] tracking-wider2 px-2 py-1 rounded-sm ${it.display_format === 'short' ? 'bg-brand-orange text-white' : 'bg-card-dark text-white'}`}>
                    {it.display_format === 'short' ? 'Short' : 'Standard'}
                  </span>
                  {replacingId === it.item_id ? (
                    <>
                      <input
                        ref={replaceInputRef}
                        value={replaceUrl}
                        onChange={(e) => setReplaceUrl(e.target.value)}
                        disabled={vidsBusy}
                        placeholder="https://www.youtube.com/shorts/…"
                        aria-label="Replacement YouTube link"
                        className="flex-1 min-w-[160px] border border-border-line rounded-sm px-3 py-2 font-body text-[13px] bg-white focus:outline-none focus:border-ink-primary disabled:opacity-50"
                        data-testid="news-video-replace-url"
                      />
                      <button onClick={saveReplace} disabled={vidsBusy} className="btn-primary !py-2 !px-3 text-[11px] disabled:opacity-50" data-testid="news-video-replace-save">
                        {vidsBusy ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={cancelReplace} disabled={vidsBusy} className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-50">Cancel</button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0 font-body text-[12px] text-ink-primary break-all">{it.source_url}</div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => moveVideo(it, -1)} disabled={vidsBusy || idx === 0} className="p-1 disabled:opacity-30" aria-label="Move video up"><ChevronUp size={14} /></button>
                        <button onClick={() => moveVideo(it, 1)} disabled={vidsBusy || idx === vids.items.length - 1} className="p-1 disabled:opacity-30" aria-label="Move video down"><ChevronDown size={14} /></button>
                      </div>
                      <button ref={(el) => { replaceBtnRefs.current[it.item_id] = el; }} onClick={() => startReplace(it)} disabled={vidsBusy} className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-50" data-testid="news-video-replace">Replace</button>
                      <button onClick={() => removeVideoItem(it)} disabled={vidsBusy} className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-50" data-testid="news-video-remove">Remove</button>
                    </>
                  )}
                </div>
              ))}
            </div>
            {vids.items.length >= 3 ? (
              <p className="font-body text-[11px] text-ink-soft">The maximum of three videos is reached — remove one to add another.</p>
            ) : vids.items.some((it) => it.display_format === 'standard') ? (
              // A widescreen video occupies the shelf alone; an active
              // Add control here would be guaranteed to fail.
              <p className="font-body text-[11px] text-ink-soft" data-testid="news-videos-standard-note">
                A widescreen video must appear alone. Remove or replace it before adding Shorts.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={addInputRef}
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  disabled={vidsBusy}
                  placeholder="https://www.youtube.com/shorts/…"
                  aria-label="YouTube video link to add"
                  className="flex-1 min-w-[240px] border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary disabled:opacity-50"
                  data-testid="news-video-url"
                />
                <button onClick={addVideo} disabled={vidsBusy} className="btn-primary !py-2 !px-4 text-[12px] disabled:opacity-50" data-testid="news-video-add">
                  {vidsBusy ? 'Saving…' : 'Add video'}
                </button>
              </div>
            )}
          </>
        )}
      </Section>

      {editing && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">
              {editing.id ? 'Edit Article' : 'New Article'}
            </h3>
            <div className="space-y-4">
              <TextField
                label="Title"
                value={editing.title}
                onChange={(v) => setEditing({ ...editing, title: v })}
                testid="news-edit-title"
              />
              <TextField
                label="Article link (opens in a new tab)"
                value={editing.url}
                onChange={(v) => setEditing({ ...editing, url: v })}
                placeholder="https://…"
                testid="news-edit-url"
              />
              <TextField
                label="Category tag (optional — the small red label, e.g. MEDIA)"
                value={editing.tag}
                onChange={(v) => setEditing({ ...editing, tag: v })}
                testid="news-edit-tag"
              />
              <div>
                <label className="block">
                  <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">Summary (optional — shows on the featured story only)</span>
                  <textarea
                    value={editing.summary || ''}
                    onChange={(e) => setEditing({ ...editing, summary: e.target.value })}
                    rows={4}
                    className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary"
                    data-testid="news-edit-summary"
                  />
                </label>
              </div>
              <ImageUploader
                value={editing.image}
                onChange={(v) => setEditing({ ...editing, image: v })}
                label="Article image (used on the featured story and cards)"
                testid="news-edit-image"
              />
              {/* No Order field: the server assigns placement on create and
                  the arrows in the list are the only way to change it. */}
              <Toggle
                label="Visible on site"
                value={editing.visible !== false}
                onChange={(v) => setEditing({ ...editing, visible: v })}
                testid="news-edit-visible"
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4" data-testid="news-edit-save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
